import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { key: "modules", label: "Modules" },
    { key: "lessons", label: "Lessons" },
    { key: "flashcards", label: "Flash Cards" },
    { key: "quizzes", label: "Quizzes" },
    { key: "questions", label: "Questions" },
    { key: "resources", label: "Resources" },
    { key: "roleplays", label: "Roleplays" },
    { key: "users", label: "Users" },
    { key: "analytics", label: "Analytics" },
];

export default function Admin() {
    const [tab, setTab] = useState("modules");
    return (
        <div className="space-y-8" data-testid="admin-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Operations</div>
                <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">Admin.</h1>
                <p className="mt-3 text-base text-ink-500">Curate content, manage trainees, and review performance.</p>
            </div>

            <div className="border-b border-ink-300 overflow-x-auto scrollbar-thin -mx-4 px-4 sm:mx-0 sm:px-0">
                <div className="flex gap-6 min-w-max">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`admin-tab-${t.key}`}
                            className={`relative pb-3 text-sm transition-colors ${tab === t.key ? "text-ink-900" : "text-ink-500 hover:text-ink-900"}`}
                        >
                            {t.label}
                            {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-haevn-700" />}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                {tab === "modules" && <AdminModulesSection />}
                {tab === "lessons" && <AdminLessonsSection />}
                {tab === "flashcards" && <AdminFlashcardsSection />}
                {tab === "quizzes" && <AdminQuizzesSection />}
                {tab === "questions" && <AdminQuestionsSection />}
                {tab === "resources" && <AdminResourcesSection />}
                {tab === "roleplays" && <AdminRoleplaysSection />}
                {tab === "users" && <Users />}
                {tab === "analytics" && <AdminAnalyticsSection />}
            </div>
        </div>
    );
}

/* -------- Generic Modal Form -------- */
function Modal({ title, onClose, onSubmit, children, submitLabel = "Save", testId }) {
    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white border border-ink-300 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid={testId}>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">Form</div>
                        <h2 className="font-editorial text-2xl mt-1">{title}</h2>
                    </div>
                    <button onClick={onClose} className="text-ink-400 hover:text-ink-900"><X size={18} /></button>
                </div>
                <form onSubmit={onSubmit} className="space-y-4">
                    {children}
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="border border-ink-300 px-4 py-2 rounded-sm text-sm hover:border-haevn-700">Cancel</button>
                        <button type="submit" className="bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900" data-testid="modal-submit-btn">{submitLabel}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const fld = "w-full bg-white border border-ink-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-haevn-700 transition-colors";
const lbl = "block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5";

function Section({ title, action, children }) {
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="font-heading text-xl text-ink-900">{title}</div>
                {action}
            </div>
            {children}
        </div>
    );
}

/* -------- Modules -------- */
export function AdminModulesSection() {
    const [items, setItems] = useState([]);
    const [edit, setEdit] = useState(null);

    const load = () => api.get("/modules").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        const f = e.target;
        const payload = {
            title: f.title.value,
            description: f.description.value,
            cover_url: f.cover_url.value || null,
            estimated_minutes: parseInt(f.estimated_minutes.value || "30", 10),
            order: parseInt(f.order.value || "0", 10),
            published: f.published.checked,
        };
        if (edit?.id) await api.put(`/modules/${edit.id}`, payload);
        else await api.post(`/modules`, payload);
        toast.success("Saved");
        setEdit(null);
        load();
    };

    const remove = async (id) => {
        if (!window.confirm("Delete module and all its content?")) return;
        await api.delete(`/modules/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <Section title="Modules" action={
            <button onClick={() => setEdit({})} data-testid="admin-new-module-btn" className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900"><Plus size={14} /> New module</button>
        }>
            <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                {items.length === 0 && <div className="p-6 text-sm text-ink-500" data-testid="admin-modules-empty">No modules yet.</div>}
                {items.map((m) => (
                    <div key={m.id} className="px-6 py-4 flex items-center justify-between gap-4" data-testid={`admin-module-row-${m.id}`}>
                        <div className="min-w-0">
                            <div className="font-heading text-base text-ink-900">{m.title}</div>
                            <div className="text-xs text-ink-500 line-clamp-1">{m.description}</div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 mt-1">order {m.order} · {m.estimated_minutes}min · {m.published ? "Published" : "Hidden"}</div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setEdit(m)} data-testid={`admin-edit-module-${m.id}`} className="p-2 text-ink-500 hover:text-ink-900"><Pencil size={14} /></button>
                            <button onClick={() => remove(m.id)} data-testid={`admin-delete-module-${m.id}`} className="p-2 text-ink-500 hover:text-clay-500"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>

            {edit && (
                <Modal title={edit.id ? "Edit module" : "New module"} onClose={() => setEdit(null)} onSubmit={save} testId="admin-module-modal">
                    <div><label className={lbl}>Title</label><input className={fld} name="title" required defaultValue={edit.title || ""} data-testid="admin-module-title" /></div>
                    <div><label className={lbl}>Description</label><textarea className={fld} name="description" rows={3} defaultValue={edit.description || ""} data-testid="admin-module-description" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className={lbl}>Estimated minutes</label><input type="number" className={fld} name="estimated_minutes" defaultValue={edit.estimated_minutes ?? 30} /></div>
                        <div><label className={lbl}>Order</label><input type="number" className={fld} name="order" defaultValue={edit.order ?? 0} /></div>
                    </div>
                    <div><label className={lbl}>Cover image URL (optional)</label><input className={fld} name="cover_url" defaultValue={edit.cover_url || ""} placeholder="https://…" /></div>
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" name="published" defaultChecked={edit.published ?? true} /> Published
                    </label>
                </Modal>
            )}
        </Section>
    );
}

/* -------- Lessons -------- */
export function AdminLessonsSection() {
    const [modules, setModules] = useState([]);
    const [moduleId, setModuleId] = useState("");
    const [lessons, setLessons] = useState([]);
    const [edit, setEdit] = useState(null);

    useEffect(() => { api.get("/modules").then((r) => { setModules(r.data); if (r.data[0]) setModuleId(r.data[0].id); }); }, []);
    useEffect(() => {
        if (!moduleId) return;
        api.get(`/modules/${moduleId}`).then((r) => setLessons(r.data.lessons));
    }, [moduleId]);

    const save = async (e) => {
        e.preventDefault();
        const f = e.target;
        const payload = {
            module_id: moduleId,
            title: f.title.value,
            kind: f.kind.value,
            content: f.content.value,
            transcript: f.transcript.value,
            order: parseInt(f.order.value || "0", 10),
            duration_seconds: parseInt(f.duration_seconds.value || "0", 10),
        };
        if (edit?.id) await api.put(`/lessons/${edit.id}`, payload);
        else await api.post(`/lessons`, payload);
        toast.success("Saved");
        setEdit(null);
        const r = await api.get(`/modules/${moduleId}`);
        setLessons(r.data.lessons);
    };

    const remove = async (id) => {
        if (!window.confirm("Delete lesson?")) return;
        await api.delete(`/lessons/${id}`);
        const r = await api.get(`/modules/${moduleId}`);
        setLessons(r.data.lessons);
    };

    return (
        <Section title="Lessons" action={
            <div className="flex items-center gap-2">
                <select value={moduleId} onChange={(e) => setModuleId(e.target.value)} data-testid="admin-lesson-module-select" className={`${fld} max-w-xs`}>
                    <option value="">Select module</option>
                    {modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                </select>
                <button onClick={() => setEdit({})} disabled={!moduleId} data-testid="admin-new-lesson-btn" className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900 disabled:opacity-50"><Plus size={14}/> New lesson</button>
            </div>
        }>
            <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                {lessons.length === 0 && <div className="p-6 text-sm text-ink-500" data-testid="admin-lessons-empty">No lessons in this module.</div>}
                {lessons.map((l) => (
                    <div key={l.id} className="px-6 py-3 flex items-center justify-between gap-4" data-testid={`admin-lesson-row-${l.id}`}>
                        <div>
                            <div className="text-sm text-ink-900">{l.title}</div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 mt-0.5">{l.kind} · order {l.order}</div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setEdit(l)} className="p-2 text-ink-500 hover:text-ink-900" data-testid={`admin-edit-lesson-${l.id}`}><Pencil size={14}/></button>
                            <button onClick={() => remove(l.id)} className="p-2 text-ink-500 hover:text-clay-500" data-testid={`admin-delete-lesson-${l.id}`}><Trash2 size={14}/></button>
                        </div>
                    </div>
                ))}
            </div>

            {edit && (
                <Modal title={edit.id ? "Edit lesson" : "New lesson"} onClose={() => setEdit(null)} onSubmit={save} testId="admin-lesson-modal">
                    <div><label className={lbl}>Title</label><input className={fld} name="title" required defaultValue={edit.title || ""} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={lbl}>Type</label>
                            <select className={fld} name="kind" defaultValue={edit.kind || "video"}>
                                <option value="video">Video</option>
                                <option value="audio">Audio</option>
                                <option value="document">Document</option>
                            </select>
                        </div>
                        <div><label className={lbl}>Order</label><input type="number" className={fld} name="order" defaultValue={edit.order ?? 0} /></div>
                    </div>
                    <div>
                        <label className={lbl}>Content (URL for video/audio, text for document)</label>
                        <textarea className={fld} name="content" rows={4} defaultValue={edit.content || ""} placeholder="https://www.youtube.com/watch?v=… OR document text" />
                    </div>
                    <div><label className={lbl}>Transcript (optional)</label><textarea className={fld} name="transcript" rows={3} defaultValue={edit.transcript || ""} /></div>
                    <div><label className={lbl}>Duration (seconds)</label><input type="number" className={fld} name="duration_seconds" defaultValue={edit.duration_seconds ?? 0} /></div>
                </Modal>
            )}
        </Section>
    );
}

/* -------- Flashcards -------- */
export function AdminFlashcardsSection() {
    const [items, setItems] = useState([]);
    const [modules, setModules] = useState([]);
    const [edit, setEdit] = useState(null);

    const load = () => api.get("/flashcards").then((r) => setItems(r.data));
    useEffect(() => { load(); api.get("/modules").then((r) => setModules(r.data)); }, []);

    const save = async (e) => {
        e.preventDefault();
        const f = e.target;
        const payload = {
            module_id: f.module_id.value || null,
            front: f.front.value,
            back: f.back.value,
            category: f.category.value || "General",
        };
        if (edit?.id) await api.put(`/flashcards/${edit.id}`, payload);
        else await api.post(`/flashcards`, payload);
        toast.success("Saved");
        setEdit(null); load();
    };

    const remove = async (id) => {
        if (!window.confirm("Delete card?")) return;
        await api.delete(`/flashcards/${id}`); load();
    };

    return (
        <Section title="Flash cards" action={
            <button onClick={() => setEdit({})} data-testid="admin-new-card-btn" className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900"><Plus size={14}/> New card</button>
        }>
            <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                {items.length === 0 && <div className="p-6 text-sm text-ink-500" data-testid="admin-cards-empty">No flash cards yet.</div>}
                {items.map((c) => (
                    <div key={c.id} className="px-6 py-3 flex items-center justify-between gap-4" data-testid={`admin-card-row-${c.id}`}>
                        <div className="min-w-0">
                            <div className="text-sm text-ink-900 line-clamp-1">{c.front}</div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 mt-0.5">{c.category}</div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setEdit(c)} className="p-2 text-ink-500 hover:text-ink-900"><Pencil size={14}/></button>
                            <button onClick={() => remove(c.id)} className="p-2 text-ink-500 hover:text-clay-500"><Trash2 size={14}/></button>
                        </div>
                    </div>
                ))}
            </div>

            {edit && (
                <Modal title={edit.id ? "Edit card" : "New card"} onClose={() => setEdit(null)} onSubmit={save} testId="admin-card-modal">
                    <div><label className={lbl}>Front</label><textarea className={fld} name="front" rows={2} required defaultValue={edit.front || ""} /></div>
                    <div><label className={lbl}>Back</label><textarea className={fld} name="back" rows={3} required defaultValue={edit.back || ""} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className={lbl}>Category</label><input className={fld} name="category" defaultValue={edit.category || "General"} placeholder="Objections, Scripts…" /></div>
                        <div>
                            <label className={lbl}>Module (optional)</label>
                            <select className={fld} name="module_id" defaultValue={edit.module_id || ""}>
                                <option value="">— None —</option>
                                {modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                            </select>
                        </div>
                    </div>
                </Modal>
            )}
        </Section>
    );
}

/* -------- Quizzes -------- */
export function AdminQuizzesSection() {
    const [items, setItems] = useState([]);
    const [modules, setModules] = useState([]);
    const [edit, setEdit] = useState(null);

    const load = () => api.get("/quizzes").then((r) => setItems(r.data));
    useEffect(() => { load(); api.get("/modules").then((r) => setModules(r.data)); }, []);

    const save = async (e) => {
        e.preventDefault();
        const f = e.target;
        const payload = {
            module_id: f.module_id.value || null,
            title: f.title.value,
            description: f.description.value,
            pass_threshold: parseInt(f.pass_threshold.value || "70", 10),
            randomize: f.randomize.checked,
            is_certification_quiz: f.is_certification_quiz?.checked ?? false,
        };
        if (edit?.id) await api.put(`/quizzes/${edit.id}`, payload);
        else await api.post(`/quizzes`, payload);
        toast.success("Saved");
        setEdit(null); load();
    };

    const remove = async (id) => { if (!window.confirm("Delete quiz?")) return; await api.delete(`/quizzes/${id}`); load(); };

    return (
        <Section title="Quizzes" action={<button onClick={() => setEdit({})} data-testid="admin-new-quiz-btn" className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900"><Plus size={14}/> New quiz</button>}>
            <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                {items.length === 0 && <div className="p-6 text-sm text-ink-500" data-testid="admin-quizzes-empty">No quizzes yet.</div>}
                {items.map((q) => (
                    <div key={q.id} className="px-6 py-3 flex items-center justify-between gap-4" data-testid={`admin-quiz-row-${q.id}`}>
                        <div>
                            <div className="text-sm text-ink-900">{q.title}</div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 mt-0.5 flex items-center gap-2">
                                <span>Pass {q.pass_threshold}%</span>
                                {q.is_certification_quiz && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 bg-haevn-700 text-white rounded-sm" data-testid={`quiz-cert-badge-${q.id}`}>
                                        Certification
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setEdit(q)} className="p-2 text-ink-500 hover:text-ink-900"><Pencil size={14}/></button>
                            <button onClick={() => remove(q.id)} className="p-2 text-ink-500 hover:text-clay-500"><Trash2 size={14}/></button>
                        </div>
                    </div>
                ))}
            </div>

            {edit && (
                <Modal title={edit.id ? "Edit quiz" : "New quiz"} onClose={() => setEdit(null)} onSubmit={save} testId="admin-quiz-modal">
                    <div><label className={lbl}>Title</label><input className={fld} name="title" required defaultValue={edit.title || ""} /></div>
                    <div><label className={lbl}>Description</label><textarea className={fld} name="description" rows={2} defaultValue={edit.description || ""} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={lbl}>Module (optional)</label>
                            <select className={fld} name="module_id" defaultValue={edit.module_id || ""}>
                                <option value="">— None —</option>
                                {modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                            </select>
                        </div>
                        <div><label className={lbl}>Pass threshold (%)</label><input type="number" className={fld} name="pass_threshold" defaultValue={edit.pass_threshold ?? 70} /></div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" name="randomize" defaultChecked={edit.randomize ?? true} /> Randomize question order
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm" data-testid="quiz-is-cert-label">
                        <input type="checkbox" name="is_certification_quiz" defaultChecked={edit.is_certification_quiz ?? false} data-testid="quiz-is-cert-checkbox" />
                        <span>
                            <span className="text-ink-900">Certification quiz</span>
                            <span className="block text-[11px] text-ink-500 mt-0.5">
                                When a Community Partner passes this quiz, HAEVN University notifies haevn.co that training is complete.
                            </span>
                        </span>
                    </label>
                </Modal>
            )}
        </Section>
    );
}

/* -------- Questions -------- */
export function AdminQuestionsSection() {
    const [quizzes, setQuizzes] = useState([]);
    const [quizId, setQuizId] = useState("");
    const [questions, setQuestions] = useState([]);
    const [edit, setEdit] = useState(null);

    useEffect(() => { api.get("/quizzes").then((r) => { setQuizzes(r.data); if (r.data[0]) setQuizId(r.data[0].id); }); }, []);
    useEffect(() => {
        if (!quizId) return;
        api.get(`/quizzes/${quizId}`).then((r) => setQuestions(r.data.questions));
    }, [quizId]);

    const reload = () => api.get(`/quizzes/${quizId}`).then((r) => setQuestions(r.data.questions));

    const save = async (e) => {
        e.preventDefault();
        const f = e.target;
        const opts = [f.opt0.value, f.opt1.value, f.opt2.value, f.opt3.value].filter((s) => s !== undefined && s.trim() !== "");
        const payload = {
            quiz_id: quizId,
            prompt: f.prompt.value,
            kind: f.kind.value,
            options: opts,
            correct_index: parseInt(f.correct_index.value || "0", 10),
            explanation: f.explanation.value,
            order: parseInt(f.order.value || "0", 10),
        };
        if (edit?.id) await api.put(`/quiz-questions/${edit.id}`, payload);
        else await api.post(`/quiz-questions`, payload);
        toast.success("Saved");
        setEdit(null); reload();
    };

    const remove = async (id) => { if (!window.confirm("Delete question?")) return; await api.delete(`/quiz-questions/${id}`); reload(); };

    return (
        <Section title="Quiz questions" action={
            <div className="flex items-center gap-2">
                <select value={quizId} onChange={(e) => setQuizId(e.target.value)} className={`${fld} max-w-xs`} data-testid="admin-question-quiz-select">
                    <option value="">Select quiz</option>
                    {quizzes.map((q) => <option key={q.id} value={q.id}>{q.title}</option>)}
                </select>
                <button onClick={() => setEdit({})} disabled={!quizId} data-testid="admin-new-question-btn" className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900 disabled:opacity-50"><Plus size={14}/> New question</button>
            </div>
        }>
            <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                {questions.length === 0 && <div className="p-6 text-sm text-ink-500" data-testid="admin-questions-empty">No questions in this quiz.</div>}
                {questions.map((q) => (
                    <div key={q.id} className="px-6 py-3 flex items-center justify-between gap-4" data-testid={`admin-question-row-${q.id}`}>
                        <div className="min-w-0">
                            <div className="text-sm text-ink-900 line-clamp-1">{q.prompt}</div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 mt-0.5">{q.kind} · {q.options.length} options</div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setEdit(q)} className="p-2 text-ink-500 hover:text-ink-900"><Pencil size={14}/></button>
                            <button onClick={() => remove(q.id)} className="p-2 text-ink-500 hover:text-clay-500"><Trash2 size={14}/></button>
                        </div>
                    </div>
                ))}
            </div>

            {edit && (
                <Modal title={edit.id ? "Edit question" : "New question"} onClose={() => setEdit(null)} onSubmit={save} testId="admin-question-modal">
                    <div><label className={lbl}>Prompt</label><textarea className={fld} name="prompt" rows={2} required defaultValue={edit.prompt || ""} /></div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className={lbl}>Type</label>
                            <select className={fld} name="kind" defaultValue={edit.kind || "mcq"}>
                                <option value="mcq">Multiple choice</option>
                                <option value="true_false">True / False</option>
                                <option value="scenario">Scenario</option>
                            </select>
                        </div>
                        <div><label className={lbl}>Correct (0-3)</label><input type="number" min={0} max={3} className={fld} name="correct_index" defaultValue={edit.correct_index ?? 0} /></div>
                        <div><label className={lbl}>Order</label><input type="number" className={fld} name="order" defaultValue={edit.order ?? 0} /></div>
                    </div>
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i}><label className={lbl}>Option {String.fromCharCode(65 + i)}</label><input className={fld} name={`opt${i}`} defaultValue={edit.options?.[i] ?? (edit.kind === "true_false" ? (i === 0 ? "True" : i === 1 ? "False" : "") : "")} /></div>
                    ))}
                    <div><label className={lbl}>Explanation (optional)</label><textarea className={fld} name="explanation" rows={2} defaultValue={edit.explanation || ""} /></div>
                </Modal>
            )}
        </Section>
    );
}

/* -------- Resources -------- */
export function AdminResourcesSection() {
    const [items, setItems] = useState([]);
    const [edit, setEdit] = useState(null);
    const load = () => api.get("/resources").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async (e) => {
        e.preventDefault();
        const f = e.target;
        const payload = {
            title: f.title.value,
            description: f.description.value,
            category: f.category.value || "General",
            file_url: f.file_url.value || null,
            body: f.body.value,
        };
        if (edit?.id) await api.put(`/resources/${edit.id}`, payload);
        else await api.post(`/resources`, payload);
        toast.success("Saved");
        setEdit(null); load();
    };
    const remove = async (id) => { if (!window.confirm("Delete resource?")) return; await api.delete(`/resources/${id}`); load(); };
    return (
        <Section title="Resources" action={<button onClick={() => setEdit({})} data-testid="admin-new-resource-btn" className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900"><Plus size={14}/> New resource</button>}>
            <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                {items.length === 0 && <div className="p-6 text-sm text-ink-500" data-testid="admin-resources-empty">No resources yet.</div>}
                {items.map((r) => (
                    <div key={r.id} className="px-6 py-3 flex items-center justify-between gap-4" data-testid={`admin-resource-row-${r.id}`}>
                        <div>
                            <div className="text-sm text-ink-900">{r.title}</div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 mt-0.5">{r.category}</div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setEdit(r)} className="p-2 text-ink-500 hover:text-ink-900"><Pencil size={14}/></button>
                            <button onClick={() => remove(r.id)} className="p-2 text-ink-500 hover:text-clay-500"><Trash2 size={14}/></button>
                        </div>
                    </div>
                ))}
            </div>
            {edit && (
                <Modal title={edit.id ? "Edit resource" : "New resource"} onClose={() => setEdit(null)} onSubmit={save} testId="admin-resource-modal">
                    <div><label className={lbl}>Title</label><input className={fld} name="title" required defaultValue={edit.title || ""} /></div>
                    <div><label className={lbl}>Description</label><textarea className={fld} name="description" rows={2} defaultValue={edit.description || ""} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className={lbl}>Category</label><input className={fld} name="category" defaultValue={edit.category || "General"} placeholder="Scripts, Cheat sheet…" /></div>
                        <div><label className={lbl}>File URL (optional)</label><input className={fld} name="file_url" defaultValue={edit.file_url || ""} placeholder="https://…" /></div>
                    </div>
                    <div><label className={lbl}>Body (optional, rich text)</label><textarea className={fld} name="body" rows={6} defaultValue={edit.body || ""} /></div>
                </Modal>
            )}
        </Section>
    );
}

/* -------- Roleplays -------- */
export function AdminRoleplaysSection() {
    const [items, setItems] = useState([]);
    const [edit, setEdit] = useState(null);
    const load = () => api.get("/roleplays").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async (e) => {
        e.preventDefault();
        const f = e.target;
        const payload = {
            title: f.title.value,
            description: f.description.value,
            audio_url: f.audio_url.value || null,
            transcript: f.transcript.value,
            takeaways: f.takeaways.value.split("\n").map((s) => s.trim()).filter(Boolean),
            tags: f.tags.value.split(",").map((s) => s.trim()).filter(Boolean),
            kind: f.kind.value,
        };
        if (edit?.id) await api.put(`/roleplays/${edit.id}`, payload);
        else await api.post(`/roleplays`, payload);
        toast.success("Saved");
        setEdit(null); load();
    };
    const remove = async (id) => { if (!window.confirm("Delete roleplay?")) return; await api.delete(`/roleplays/${id}`); load(); };
    return (
        <Section title="Roleplays" action={<button onClick={() => setEdit({})} data-testid="admin-new-roleplay-btn" className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900"><Plus size={14}/> New roleplay</button>}>
            <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                {items.length === 0 && <div className="p-6 text-sm text-ink-500" data-testid="admin-roleplays-empty">No roleplays yet.</div>}
                {items.map((r) => (
                    <div key={r.id} className="px-6 py-3 flex items-center justify-between gap-4" data-testid={`admin-roleplay-row-${r.id}`}>
                        <div>
                            <div className="text-sm text-ink-900">{r.title}</div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 mt-0.5">{r.kind}</div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setEdit(r)} className="p-2 text-ink-500 hover:text-ink-900"><Pencil size={14}/></button>
                            <button onClick={() => remove(r.id)} className="p-2 text-ink-500 hover:text-clay-500"><Trash2 size={14}/></button>
                        </div>
                    </div>
                ))}
            </div>
            {edit && (
                <Modal title={edit.id ? "Edit roleplay" : "New roleplay"} onClose={() => setEdit(null)} onSubmit={save} testId="admin-roleplay-modal">
                    <div><label className={lbl}>Title</label><input className={fld} name="title" required defaultValue={edit.title || ""} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={lbl}>Type</label>
                            <select className={fld} name="kind" defaultValue={edit.kind || "good"}>
                                <option value="good">Good call</option>
                                <option value="bad">Bad call</option>
                                <option value="improved">Improved</option>
                                <option value="objection">Objection</option>
                                <option value="voicemail">Voicemail</option>
                                <option value="discovery">Discovery</option>
                            </select>
                        </div>
                        <div><label className={lbl}>Audio URL</label><input className={fld} name="audio_url" defaultValue={edit.audio_url || ""} placeholder="https://…/audio.mp3" /></div>
                    </div>
                    <div><label className={lbl}>Description</label><textarea className={fld} name="description" rows={2} defaultValue={edit.description || ""} /></div>
                    <div><label className={lbl}>Transcript</label><textarea className={fld} name="transcript" rows={5} defaultValue={edit.transcript || ""} /></div>
                    <div><label className={lbl}>Takeaways (one per line)</label><textarea className={fld} name="takeaways" rows={3} defaultValue={(edit.takeaways || []).join("\n")} /></div>
                    <div><label className={lbl}>Tags (comma separated)</label><input className={fld} name="tags" defaultValue={(edit.tags || []).join(", ")} /></div>
                </Modal>
            )}
        </Section>
    );
}

/* -------- Users -------- */
function Users() {
    const [users, setUsers] = useState([]);
    const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
    useEffect(() => { load(); }, []);

    const updateRole = async (id, role) => {
        await api.put(`/admin/users/${id}`, { role });
        toast.success("Updated");
        load();
    };
    const remove = async (id) => {
        if (!window.confirm("Delete user?")) return;
        await api.delete(`/admin/users/${id}`); load();
    };

    return (
        <Section title="Users">
            <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                {users.length === 0 && <div className="p-6 text-sm text-ink-500" data-testid="admin-users-empty">No users.</div>}
                {users.map((u) => (
                    <div key={u.id} className="px-6 py-4 flex items-center justify-between gap-4" data-testid={`admin-user-row-${u.id}`}>
                        <div>
                            <div className="text-sm text-ink-900">{u.name}</div>
                            <div className="text-xs text-ink-500">{u.email}</div>
                        </div>
                        <div className="flex items-center gap-3">
                            <select value={u.role} onChange={(e) => updateRole(u.id, e.target.value)} data-testid={`admin-user-role-${u.id}`} className={fld}>
                                <option value="trainee">Trainee</option>
                                <option value="trainer">Trainer</option>
                                <option value="admin">Admin</option>
                            </select>
                            <button onClick={() => remove(u.id)} className="p-2 text-ink-500 hover:text-clay-500" data-testid={`admin-user-delete-${u.id}`}><Trash2 size={14}/></button>
                        </div>
                    </div>
                ))}
            </div>
        </Section>
    );
}

/* -------- Analytics -------- */
export function AdminAnalyticsSection() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="text-sm text-ink-500" data-testid="admin-analytics-loading">Loading…</div>;
    return (
        <Section title="Analytics">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-300 border border-ink-300">
                <Stat label="Users" value={data.users} />
                <Stat label="Trainees" value={data.trainees} />
                <Stat label="Modules" value={data.modules} />
                <Stat label="Quiz pass rate" value={`${data.pass_rate}%`} />
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono mb-3 mt-6">Module completion</div>
                <div className="bg-white border border-ink-300 divide-y divide-ink-200">
                    {data.module_stats.length === 0 && <div className="p-6 text-sm text-ink-500">No modules yet.</div>}
                    {data.module_stats.map((m) => (
                        <div key={m.module_id} className="px-6 py-4 grid md:grid-cols-[1fr_240px_60px] gap-6 items-center" data-testid={`admin-module-stat-${m.module_id}`}>
                            <div className="text-sm text-ink-900">{m.title}</div>
                            <div className="w-full h-1.5 bg-ink-200 rounded-full overflow-hidden"><div className="h-full bg-haevn-700" style={{ width: `${m.completion_rate}%` }} /></div>
                            <div className="text-sm text-ink-500 font-mono text-right">{m.completion_rate}%</div>
                        </div>
                    ))}
                </div>
            </div>
        </Section>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{label}</div>
            <div className="mt-3 font-heading text-3xl text-ink-900">{value}</div>
        </div>
    );
}
